/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';
import {
  BonusItem,
  CustomerProfile,
  CustomerType,
  CustomerDeliveryAddress,
  TokenizedPaymentCard,
  FormValidationResult,
  CachedRecentOrFavoriteOrders,
  RecentOrFavoriteOrder,
} from '@/models';
import { prefixValues } from '@/helpers/util';
import { Promotion, RewardPoints, PointsActivity } from '@/models';
import { SustainabilityMetrics } from '@/models/sustainability';

export interface CustomerModuleState {
  jwt: string;
  optedIntoEmailMarketingFromOptions: boolean;
  optedIntoPersonalizationMessaging: boolean;
  profile: CustomerProfile | null;
  newAccount: boolean;
  type: CustomerType | null;
  rewardPoints: RewardPoints | null;
  favoriteOrders: { [key: number]: CachedRecentOrFavoriteOrders };
  recentOrders: { [key: number]: CachedRecentOrFavoriteOrders };
  wallet: {
    promotions: Promotion[];
    bonuses: BonusItem[];
    paymentCards: TokenizedPaymentCard[];
    giftCardBalances: { [key: string]: number };
  };
  deliveryAddresses: CustomerDeliveryAddress[];
  favoriteRestaurants: { [key: number]: string };
  loyalty: {
    forced: boolean | null;
    isEnrolled: boolean;
    declined: boolean;
    pointsHistory: PointsActivity[];
    daysTillPointsExpire: number;
    daysPointsExpired: number;
    joinedLoyaltyOnOrderConfirmationPage: boolean;
    optedIntoSummerOfExtrasYear: number;
  };
  celebration: {
    numberOfRewards: number;
    points: number;
    lastUpdated: Date | null;
  };
  validationResult: FormValidationResult | undefined;
  sustainabilityMetrics: {
    order: SustainabilityMetrics | null;
    history: SustainabilityMetrics | null;
    sustainabilityTooltipAppeared: boolean;
  };
  profileSubscriptionChangeOnly: boolean;
}

const modulePrefix = 'customer/';
const MUTATION_NAMES = {
  SET_JWT: 'setJwt',
  SET_OPTED_INTO_EMAIL_MARKETING_FROM_OPTIONS: 'setOptedIntoEmailMarketingFromOptions',
  SET_OPTED_INTO_PERSONALIZED_MESSAGING: 'setOptedIntoPersonalizedMessaging',
  RESET_PROFILE: 'resetProfile',
  REFRESH_PROFILE: 'refreshProfile',
  SET_FAVORITE_ORDERS: 'setFavoriteOrders',
  SET_RECENT_ORDERS: 'setRecentOrders',
  SET_WALLET_PROMOTIONS: 'setWalletPromotions',
  SET_WALLET_BONUSES: 'setWalletBonuses',
  SET_WALLET_PAYMENT_CARDS: 'setWalletPaymentCards',
  SET_WALLET_GIFT_CARD_BALANCES: 'setWalletGiftCardBalances',
  SET_DELIVERY_ADDRESSES: 'setDeliveryAddresses',
  SET_DAYS_POINTS_EXPIRED: 'setDaysPointsExpired',
  SET_DAYS_TILL_POINTS_EXPIRE: 'setDaysTillPointsExpire',
  SET_REWARD_POINTS: 'setRewardPoints',
  SET_FAVORITE_RESTAURANTS: 'setFavoriteRestaurants',
  SET_CUSTOMER_TYPE: 'setCustomerType',
  SET_NEW_ACCOUNT: 'setNewAccount',
  SET_LOYALTY_FORCED: 'setLoyaltyForced',
  SET_LOYALTY_IS_ENROLLED: 'setLoyaltyIsEnrolled',
  SET_OPTED_INTO_SUMMER_OF_EXTRAS_YEAR: 'setOptedIntoSummerOfExtrasYear',
  SET_LOYALTY_DECLINED: 'setLoyaltyDeclined',
  SET_JOINED_LOYALTY_ON_ORDER_CONFIRMATION_PAGE: 'joinedLoyaltyOnOrderConfirmationPage',
  SET_LOYALTY_POINTS_HISTORY: 'setLoyaltyPointsHistory',
  SET_CELEBRATION_NUMBER_OF_REWARDS: 'setNumberOfRewards',
  SET_CELEBRATION_POINTS: 'setCelebrationPoints',
  SET_CELEBRATION_LAST_UPDATED: 'setCelebrationLastUpdated',
  SET_CARD_VALIDATION_RESULT: 'setCardValidationResult',
  SET_CUSTOMER_PROFILE: 'setCustomerProfile',
  SET_SUSTAINABILITY_HISTORY_METRICS: 'setSustainabilityHistoryMetrics',
  SET_SUSTAINABILITY_ORDER_METRICS: 'setSustainabilityOrderMetrics',
  SET_SUSTAINABILITY_TOOLTIP_APPEARED: 'setSustainabilityTooltipAppeared',
  SET_PROFILE_SUBSCRIPTION_CHANGE_ONLY: 'setProfileSubscriptionChangeOnly',
};

const GETTER_NAMES = {
  FAVORITE_ORDERS: 'currentFavoriteOrders',
  FAVORITE_ORDERS_EXPIRATION: 'currentFavoriteOrdersExpiration',
  RECENT_ORDERS: 'currentRecentOrders',
  RECENT_ORDERS_EXPIRATION: 'currentRecentOrdersExpiration',
  HAS_JWT: 'hasJwt',
  IS_LOGGED_IN: 'isLoggedIn',
  IS_ACTIVE_STUDENT: 'isActiveStudent',
  IS_CYOB_REWARD_ELIGIBLE: 'isCyobRewardEligible',
  MOST_RECENT_BIRTHDAY_YEAR: 'mostRecentBirthdayYear',
  HAS_SEEN_CYOB_REWARDS_MODAL: 'hasSeenCyobRewardsModal',
  HAS_REDEEMED_CYOB_REWARD: 'hasRedeemedCyobReward',
  IS_OPTED_IN_SUMMER_OF_CHIPOTLE: 'isOptedInSummerOfChipotle',
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, modulePrefix);
export const GETTERS = prefixValues(GETTER_NAMES, modulePrefix);

export function makeCustomerModule(): Module<CustomerModuleState, AppState> {
  return {
    namespaced: true,
    state: {
      jwt: '',
      optedIntoEmailMarketingFromOptions: false,
      optedIntoPersonalizationMessaging: false,
      profile: null,
      type: null,
      newAccount: false,
      rewardPoints: null,
      favoriteOrders: [],
      recentOrders: [],
      wallet: {
        promotions: [],
        bonuses: [],
        paymentCards: [],
        giftCardBalances: {}
      },
      deliveryAddresses: [],
      favoriteRestaurants: {},
      loyalty: {
        forced: null,
        isEnrolled: false,
        declined: false,
        pointsHistory: [],
        daysTillPointsExpire: 0,
        daysPointsExpired: 0,
        joinedLoyaltyOnOrderConfirmationPage: false,
        optedIntoSummerOfExtrasYear: 2025
      },
      celebration: {
        numberOfRewards: 0,
        points: 0,
        lastUpdated: null,
      },
      validationResult: undefined,
      sustainabilityMetrics: {
        order: null,
        history: null,
        sustainabilityTooltipAppeared: false,
      },
      profileSubscriptionChangeOnly: false,
    },
    mutations: {
      [MUTATION_NAMES.SET_JWT]: (state, jwt: string) => {
        state.jwt = jwt;
      },
      [MUTATION_NAMES.SET_OPTED_INTO_EMAIL_MARKETING_FROM_OPTIONS]: (state, optedIn) => {
        state.optedIntoEmailMarketingFromOptions = optedIn;
      },            
      [MUTATION_NAMES.SET_OPTED_INTO_PERSONALIZED_MESSAGING]: (state, optedIn) => {
        state.optedIntoPersonalizationMessaging = optedIn;
      },
      [MUTATION_NAMES.RESET_PROFILE]: state => {
        state.profile = null;
      },
      [MUTATION_NAMES.REFRESH_PROFILE]: (state, profile: CustomerProfile) => {
        state.profile = { ...(state.profile || {}), ...profile };
      },
      [MUTATION_NAMES.SET_FAVORITE_ORDERS]: (state, orders: { [key: number]: CachedRecentOrFavoriteOrders }) => {
        state.favoriteOrders = orders;
      },
      [MUTATION_NAMES.SET_RECENT_ORDERS]: (state, orders: { [key: number]: CachedRecentOrFavoriteOrders }) => {
        state.recentOrders = orders;
      },
      [MUTATION_NAMES.SET_WALLET_PROMOTIONS]: (state, promotions: Promotion[]) => {
        state.wallet.promotions = promotions;
      },
      [MUTATION_NAMES.SET_WALLET_BONUSES]: (state, bonuses: BonusItem[]) => {
        state.wallet.bonuses = bonuses;
      },
      [MUTATION_NAMES.SET_WALLET_PAYMENT_CARDS]: (state, paymentCards: TokenizedPaymentCard[]) => {
        state.wallet.paymentCards = paymentCards;
      },
      [MUTATION_NAMES.SET_WALLET_GIFT_CARD_BALANCES]: (state, giftCardBalances) => {
        state.wallet.giftCardBalances = giftCardBalances;
      },
      [MUTATION_NAMES.SET_DELIVERY_ADDRESSES]: (state, deliveryAddresses: []) => {
        state.deliveryAddresses = deliveryAddresses;
      },
      [MUTATION_NAMES.SET_DAYS_TILL_POINTS_EXPIRE]: (state, days: number) => {
        state.loyalty.daysTillPointsExpire = days;
      },
      [MUTATION_NAMES.SET_DAYS_POINTS_EXPIRED]: (state, days: number) => {
        state.loyalty.daysPointsExpired = days;
      },
      [MUTATION_NAMES.SET_REWARD_POINTS]: (state, points: RewardPoints) => {
        state.rewardPoints = points;
      },
      [MUTATION_NAMES.SET_FAVORITE_RESTAURANTS]: (state, favoriteRestaurants: { [key: number]: string }) => {
        state.favoriteRestaurants = favoriteRestaurants;
      },
      [MUTATION_NAMES.SET_NEW_ACCOUNT]: (state, value: boolean) => {
        state.newAccount = value;
      },
      [MUTATION_NAMES.SET_CUSTOMER_TYPE]: (state, type: CustomerType) => {
        state.type = type;
      },
      [MUTATION_NAMES.SET_LOYALTY_FORCED](state, forced: boolean) {
        state.loyalty.forced = forced;
      },
      [MUTATION_NAMES.SET_LOYALTY_IS_ENROLLED](state, isEnrolled: boolean) {
        state.loyalty.isEnrolled = isEnrolled;
      },
      [MUTATION_NAMES.SET_LOYALTY_DECLINED](state, declined: boolean) {
        state.loyalty.declined = declined;
      },
      [MUTATION_NAMES.SET_OPTED_INTO_SUMMER_OF_EXTRAS_YEAR](state, year: number) {
        state.loyalty.optedIntoSummerOfExtrasYear = year;
      },
      [MUTATION_NAMES.SET_LOYALTY_POINTS_HISTORY](state, pointsActivity: PointsActivity[]) {
        state.loyalty.pointsHistory = pointsActivity;
      },
      [MUTATION_NAMES.SET_CELEBRATION_NUMBER_OF_REWARDS](state, numberOfRewards: number) {
        state.celebration.numberOfRewards = numberOfRewards;
      },
      [MUTATION_NAMES.SET_CELEBRATION_POINTS](state, points: number) {
        state.celebration.points = points;
      },
      [MUTATION_NAMES.SET_CELEBRATION_LAST_UPDATED](state, lastUpdated: Date) {
        state.celebration.lastUpdated = lastUpdated;
      },
      [MUTATION_NAMES.SET_CARD_VALIDATION_RESULT](state, validationResult: FormValidationResult) {
        state.validationResult = validationResult;
      },
      [MUTATION_NAMES.SET_CUSTOMER_PROFILE](state, profile: CustomerProfile | null) {
        state.profile = profile;
      },
      [MUTATION_NAMES.SET_SUSTAINABILITY_HISTORY_METRICS](state, sustainabilityHistoryMetrics: SustainabilityMetrics | null) {
        state.sustainabilityMetrics.history = sustainabilityHistoryMetrics;
      },
      [MUTATION_NAMES.SET_SUSTAINABILITY_ORDER_METRICS](state, sustainabilityOrderMetrics: SustainabilityMetrics | null) {
        state.sustainabilityMetrics.order = sustainabilityOrderMetrics;
      },
      [MUTATION_NAMES.SET_SUSTAINABILITY_TOOLTIP_APPEARED](state, tooltipAppeared: boolean) {
        state.sustainabilityMetrics.sustainabilityTooltipAppeared = tooltipAppeared;
      },
      [MUTATION_NAMES.SET_JOINED_LOYALTY_ON_ORDER_CONFIRMATION_PAGE](state, joinedLoyaltyOnOrderConfirmationPage: boolean) {
        state.loyalty.joinedLoyaltyOnOrderConfirmationPage = joinedLoyaltyOnOrderConfirmationPage;
      },
      [MUTATION_NAMES.SET_PROFILE_SUBSCRIPTION_CHANGE_ONLY]: (state, value: boolean) => {
        // This is used to determine whether the user is changing only profile info or Subscription preferences
        // in the personal-and-preferences page. This is used to determine what additional api calls need to be made
        // on customer refresh
        state.profileSubscriptionChangeOnly = value;
      },
    },
    getters: {
      [GETTER_NAMES.FAVORITE_ORDERS]: (state, getters, rootState): RecentOrFavoriteOrder[] => {
        if (!rootState.restaurant.selectedRestaurant) {
          return [];
        }
        const cached = state.favoriteOrders[rootState.restaurant.selectedRestaurant.restaurantNumber];
        return cached?.orders || [];
      },
      [GETTER_NAMES.FAVORITE_ORDERS_EXPIRATION]: (state, getters, rootState): Date | null => {
        if (!rootState.restaurant.selectedRestaurant) {
          return null;
        }
        const cached = state.favoriteOrders[rootState.restaurant.selectedRestaurant.restaurantNumber];
        return cached?.expiration ?? null;
      },
      [GETTER_NAMES.RECENT_ORDERS]: (state, getters, rootState): RecentOrFavoriteOrder[] => {
        if (!rootState.restaurant.selectedRestaurant) {
          return [];
        }

        const cached = state.recentOrders[rootState.restaurant.selectedRestaurant.restaurantNumber];
        return cached?.orders || [];
      },
      [GETTER_NAMES.RECENT_ORDERS_EXPIRATION]: (state, getters, rootState): Date | null => {
        if (!rootState.restaurant.selectedRestaurant) {
          return null;
        }
        const cached = state.recentOrders[rootState.restaurant.selectedRestaurant.restaurantNumber];
        return cached?.expiration ?? null;
      },
      [GETTER_NAMES.HAS_JWT]: state => !!state.jwt,
      [GETTER_NAMES.IS_LOGGED_IN]: (state): boolean | undefined => {
        const { profile, jwt } = state;
        return jwt && !profile ? undefined : !!(profile && !profile.isGuest);
      },
      [GETTER_NAMES.IS_ACTIVE_STUDENT]: (state): boolean => {
        return state.profile?.student?.studentStatus?.toLowerCase() === 'active';
      },
      [GETTER_NAMES.IS_CYOB_REWARD_ELIGIBLE]: (state, getters): boolean => {
        const { daysSinceBirthday, cyobRedeemedForYear } = state.profile || {};
        if (daysSinceBirthday == null) return false;

        const mostRecentBirthdayYear = getters.mostRecentBirthdayYear
        
        const hasSelectedCyobReward = !!(cyobRedeemedForYear && (mostRecentBirthdayYear <= cyobRedeemedForYear));

        const isWithin30Days = daysSinceBirthday <= 30;

        return !hasSelectedCyobReward && isWithin30Days;
      },
      [GETTER_NAMES.MOST_RECENT_BIRTHDAY_YEAR]: (state): number | undefined => {
        const { daysSinceBirthday } = state.profile || {};
        if (daysSinceBirthday == null) return;
        
        const mostRecentBirthday = new Date();
        mostRecentBirthday.setDate(mostRecentBirthday.getDate() - daysSinceBirthday);
        return mostRecentBirthday.getFullYear();
      },
      [GETTER_NAMES.HAS_SEEN_CYOB_REWARDS_MODAL]: (state, getters): boolean => {
        const seenYear = state.profile?.cyobLastSeenYear;
        const mostRecentBirthdayYear = getters.mostRecentBirthdayYear;
        return !!(seenYear && mostRecentBirthdayYear && mostRecentBirthdayYear <= seenYear);
      },
      [GETTER_NAMES.HAS_REDEEMED_CYOB_REWARD]: (state, getters): boolean => {
        return !!(state.profile?.cyobRedeemedForYear && (getters.mostRecentBirthdayYear <= state.profile?.cyobRedeemedForYear));
      },
      [GETTER_NAMES.IS_OPTED_IN_SUMMER_OF_CHIPOTLE]: (state): boolean => {
        const currentYear = new Date().getFullYear();
        return state.loyalty.optedIntoSummerOfExtrasYear === currentYear;
      },
    },
  };
}
